/**
 * PendingApprovalPage — shown when a signed-in user has status="pending".
 *
 * Also handles the forgot-password success receipt (in the same visual
 * style) to keep messaging consistent.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import ConnectionSourceLogo from "../components/ConnectionSourceLogo";
import { HourglassMedium, SignOut, EnvelopeSimple, ArrowClockwise } from "@phosphor-icons/react";

export default function PendingApprovalPage() {
  const { user, logout, refreshUser } = useAuth();
  const polling = useRef(null);

  // Gentle polling — when admin approves we hop straight into the app.
  useEffect(() => {
    polling.current = setInterval(async () => {
      try {
        const next = await refreshUser?.();
        if (next?.status && next.status !== "pending") {
          toast.success("You're approved! Welcome aboard 🎉");
          window.location.href = "/me";
        }
      } catch { /* noop */ }
    }, 30_000);
    return () => clearInterval(polling.current);
  }, [refreshUser]);

  const onRefresh = async () => {
    try {
      const next = await refreshUser?.();
      if (next?.status && next.status !== "pending") {
        toast.success("Approved! Taking you in…");
        window.location.href = "/me";
      } else {
        toast.info("Still waiting on admin approval.");
      }
    } catch {
      toast.error("Could not check status");
    }
  };

  return (
    <div className="cs-login-root min-h-screen relative flex items-center justify-center p-4 overflow-hidden">
      <div className="cs-login-card relative z-10 w-full max-w-md rounded-3xl overflow-hidden">
        <div
          aria-hidden
          className="absolute top-0 left-0 right-0 h-[2px]"
          style={{ background: "linear-gradient(90deg, transparent, #F59E0B, #CD040B, #F59E0B, transparent)" }}
        />
        <div className="relative p-7 text-center">
          <div className="flex justify-center mb-5">
            <ConnectionSourceLogo size="md" />
          </div>
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, rgba(245,158,11,0.22), rgba(245,158,11,0.08))",
              border: "1px solid rgba(245,158,11,0.45)",
              boxShadow: "0 0 24px rgba(245,158,11,0.35)",
              color: "#F59E0B",
            }}
          >
            <HourglassMedium size={30} weight="duotone" className="cs-pending-hourglass" />
          </div>
          <h1
            className="text-2xl font-black mb-1"
            style={{ fontFamily: "Outfit, sans-serif", color: "var(--cs-text)" }}
          >
            Awaiting approval
          </h1>
          <p className="text-sm mb-5" style={{ color: "var(--cs-text-muted)" }}>
            Thanks for signing up, <strong>{user?.name?.split(" ")[0] || "there"}</strong>!
            An admin will pair your account with a rep name shortly — this page
            refreshes the moment that happens.
          </p>

          <div
            className="flex items-center gap-2 p-3 rounded-xl mb-4"
            style={{ background: "var(--cs-bg-hover)", border: "1px solid var(--cs-border)" }}
          >
            <EnvelopeSimple size={14} className="shrink-0" style={{ color: "var(--cs-text-muted)" }} />
            <div className="text-[11px] text-left truncate" style={{ color: "var(--cs-text-muted)" }}>
              <span style={{ color: "var(--cs-text)" }}>{user?.email}</span>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={onRefresh}
              className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black transition-all active:scale-95"
              style={{
                background: "linear-gradient(135deg, #CD040B, #8B0308)",
                color: "#FFF",
                boxShadow: "0 8px 20px -6px rgba(205,4,11,0.55)",
              }}
              data-testid="pending-refresh-btn"
            >
              <ArrowClockwise size={13} weight="bold" />
              Check status
            </button>
            <button
              onClick={() => logout()}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold transition-all active:scale-95"
              style={{ background: "var(--cs-bg-hover)", color: "var(--cs-text-muted)", border: "1px solid var(--cs-border)" }}
              data-testid="pending-logout-btn"
            >
              <SignOut size={13} weight="bold" /> Sign out
            </button>
          </div>
          <p className="text-[10px] mt-5" style={{ color: "var(--cs-text-muted)" }}>
            We check automatically every 30 seconds.
          </p>
        </div>
      </div>
    </div>
  );
}
